import { useEffect, useState } from 'react';
import api from '../lib/api';
import { FileUploadField } from '../components/FileUploadField';
import { DocumentLabelEditor } from './DocumentLabels';

const byNewest = (a, b) => (b.created_at || '').localeCompare(a.created_at || '');

export const CareDocumentsTab = ({ patientUserId, legacyPatientId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get('/storage/patient-files', { params: { patient_user_id: patientUserId || '', patient_id: legacyPatientId || '' } })
      .then((r) => { if (live) { setFiles(r.data.files || []); setError(''); } })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || 'Could not load documents'); })
      .finally(() => { if (live) setLoading(false); });
    api.get('/storage/labels').then((r) => { if (live) setOptions([...r.data.preset, ...r.data.custom]); }).catch(() => {});
    return () => { live = false; };
  }, [patientUserId, legacyPatientId]);

  const onChange = (next) => {
    const known = new Set(files.map((f) => f.file_id));
    if (next.some((f) => !known.has(f.file_id))) {
      setFilter('');
      setFiles([...next.filter((f) => !known.has(f.file_id)), ...files].map((f) => (f.origin_label ? f : { ...f, origin_label: 'Staff upload' })).sort(byNewest));
      return;
    }
    const kept = new Set(next.map((f) => f.file_id));
    const hidden = files.filter((f) => !kept.has(f.file_id) && filter && !(f.labels || []).includes(filter));
    setFiles([...next, ...hidden].map((f) => (f.origin_label ? f : { ...f, origin_label: 'Staff upload' })).sort(byNewest));
  };
  const onSaved = (id, labels) => {
    setFiles((prev) => prev.map((f) => (f.file_id === id ? { ...f, labels } : f)));
    setOptions((prev) => [...new Set([...prev, ...labels])]);
  };
  const used = [...new Set(files.flatMap((f) => f.labels || []))];
  useEffect(() => { if (filter && !used.includes(filter)) setFilter(''); }, [filter, used.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = filter ? files.filter((f) => (f.labels || []).includes(filter)) : files;

  return (
    <div className="space-y-3" data-testid="care-customer-documents">
      <div>
        <h3 className="text-sm font-semibold">Documents</h3>
        <p className="text-xs text-muted-foreground">Patient portal uploads, files added by staff and prescription scans from POS sales. Click a file to open it.</p>
      </div>
      {used.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="care-documents-filter">
          {['', ...used].map((l) => (
            <button key={l || 'all'} type="button" onClick={() => setFilter(l)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === l ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
              data-testid={`care-documents-filter-${l ? l.toLowerCase().replace(/\s+/g, '-') : 'all'}`}>
              {l || 'All'}
            </button>
          ))}
        </div>
      )}
      {error && <p role="alert" className="text-xs text-red-700" data-testid="care-documents-error">{error}</p>}
      {loading ? <p className="text-sm text-muted-foreground" data-testid="care-documents-loading">Loading documents…</p> : (
        <>
          {!shown.length && <p className="care-empty" data-testid="care-documents-empty">{filter ? `No documents labelled "${filter}".` : 'No documents for this client yet.'}</p>}
          <FileUploadField
            files={shown}
            onChange={onChange}
            category="patient_document"
            entityType="patient"
            entityId={legacyPatientId || patientUserId}
            max={100}
            deleteOnRemove
            label=""
            testId="care-documents"
            renderExtra={(f, i) => <DocumentLabelEditor file={f} index={i} options={options} onSaved={onSaved} />}
          />
        </>
      )}
    </div>
  );
};
